import * as React from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover } from '@/components/ui/popover';

export interface ComboboxOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface ComboboxProps {
  value: string;
  onChange: (value: string) => void;
  options: ComboboxOption[];
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  /** Maximum dropdown height (defaults to 60vh). */
  maxHeight?: string;
}

/** Custom dropdown — same visual language as the rest of the system
 *  (button trigger, portal-rendered popover, hover/active states). Used
 *  where native <select> styling can't match the design (forms, modals). */
export function Combobox({
  value, onChange, options, placeholder, disabled, id, className, maxHeight = '60vh',
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const [triggerWidth, setTriggerWidth] = React.useState<number>(0);

  React.useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    setTriggerWidth(triggerRef.current.getBoundingClientRect().width);
  }, [open]);

  const selected = options.find(o => o.value === value);

  return (
    <>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'flex h-9 w-full items-center justify-between gap-2 rounded-lg border border-input bg-card px-3 text-sm font-medium text-foreground shadow-sm transition-colors',
          'hover:border-primary/40 hover:bg-muted/60',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:border-ring',
          'disabled:cursor-not-allowed disabled:opacity-50',
          open && 'border-primary/50 bg-muted/60 ring-2 ring-ring/30',
          className,
        )}
      >
        <span className={cn('flex-1 truncate text-left', !selected && 'font-normal text-muted-foreground')}>
          {selected ? selected.label : (placeholder ?? '')}
        </span>
        <ChevronDown
          className={cn(
            'size-4 shrink-0 text-muted-foreground/70 transition-transform duration-150',
            open && 'rotate-180 text-primary',
          )}
        />
      </button>
      <Popover open={open} onOpenChange={setOpen} triggerRef={triggerRef} side="bottom" align="start">
        <div
          role="listbox"
          className="flex flex-col overflow-y-auto thin-scrollbar p-1"
          style={{ minWidth: triggerWidth, maxHeight }}
        >
          {options.map(opt => {
            const active = opt.value === value;
            return (
              <button
                key={opt.value}
                role="option"
                type="button"
                aria-selected={active}
                disabled={opt.disabled}
                onClick={() => { onChange(opt.value); setOpen(false); }}
                className={cn(
                  'flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                  active ? 'bg-primary-muted text-primary-muted-foreground' : 'hover:bg-muted hover:text-foreground',
                  opt.disabled && 'opacity-50 cursor-not-allowed',
                )}
              >
                <span className="flex-1 whitespace-nowrap">{opt.label}</span>
                {active && <Check className="size-4 shrink-0" />}
              </button>
            );
          })}
        </div>
      </Popover>
    </>
  );
}
